/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Select, SelectItem, Spinner, User } from '@nextui-org/react';
import { IconArrowLeft } from '@tabler/icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Constants, classNames, copy, fromPhotos } from '../../../utils';
import { IconAdminMenu3, IconTeacher } from '../../../assets/icons';
import { SchoolService, UsersService } from '../../../services';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const LEVEL = {
  STUDENT: 1,
  TEACHER: 2,
}

const AssignmentsCreate = () => {
  const navigate = useNavigate();
  const { filterBy, form, grades, isLoading, onChange, schoolYears, sections, filters, onSubmit } = useFetchTable();
  const list = form.level === LEVEL.STUDENT ? form.students : form.teachers;

  const tableName = useMemo(() => {
    if (form.level === LEVEL.STUDENT) return 'Estudiantes';
    return 'Profesores';
  }, [form.level])


  return (
    <div className="flex justify-between gap-4">
      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <aside className="bg-foreground-200 w-60 h-full p-4 rounded-lg flex flex-col gap-4">
        <div className="flex gap-4">
          <IconArrowLeft className="cursor-pointer" onClick={() => navigate('/assignments', { replace: true })} />
          <span className="font-bold text-secondary">Asignar</span>
        </div>

        <Select
          label="Año escolar"
          labelPlacement="outside"
          placeholder=" "
          variant="bordered"
          radius="sm"
          classNames={{
            label: 'font-semibold',
            base: 'w-full',
            trigger: 'border-1',
          }}
          disallowEmptySelection={true}
          selectedKeys={!!filters.school_year_id ? [filters.school_year_id] : []}
          onSelectionChange={v => filterBy(v.currentKey, 'school_year_id')}
        >
          {schoolYears.map((item) => (
            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
          ))}
        </Select>

        <Select
          label="Grado"
          labelPlacement="outside"
          placeholder=" "
          variant="bordered"
          radius="sm"
          classNames={{
            label: 'font-semibold',
            base: 'w-full',
            trigger: 'border-1',
          }}
          disallowEmptySelection={true}
          selectedKeys={!!filters.school_grade_id ? [filters.school_grade_id] : []}
          onSelectionChange={v => filterBy(v.currentKey, 'school_grade_id')}
        >
          {grades.map((item) => (
            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
          ))}
        </Select>

        <Button
          variant="flat"
          className="bg-green text-white font-semibold"
          onClick={onSubmit}
        >
          Guardar
        </Button>
      </aside>

      <div className="flex flex-1 flex-col">
        <div className="flex flex-row justify-center items-start gap-6">
          <Button
            className={classNames(
              'h-12 font-semibold',
              form.level === LEVEL.STUDENT
                ? 'bg-primary border-primary text-white'
                : 'bg-white',
            )}
            variant="bordered"
            radius="sm"
            onClick={() => onChange(LEVEL.STUDENT, 'level')}
            startContent={
              <img
                src={IconAdminMenu3}
                className={form.level === LEVEL.STUDENT ? '' : 'invert brightness-75'}
                alt=""
              />
            }
          >
            Asignar Estudiante
          </Button>
          <Button
            className={classNames(
              'h-12 font-semibold',
              form.level === LEVEL.TEACHER
                ? 'bg-primary border-primary text-white'
                : 'bg-white',
            )}
            variant="bordered"
            radius="sm"
            onClick={() => onChange(LEVEL.TEACHER, 'level')}
            startContent={
              <img
                src={IconTeacher}
                className={form.level === LEVEL.TEACHER ? 'invert brightness-0' : ''}
                alt=""
              />
            }
          >
            Asignar Profesores
          </Button>
        </div>

        <main className="mt-4 text-sm last:rounded-b-lg last:overflow-hidden">
          <div
            className={classNames('grid rounded-t-lg bg-foreground-500 text-white [&>div]:p-2')}
            style={{ gridTemplateColumns: `repeat(${2 + sections?.length}, minmax(0, 1fr))` }}
          >
            <div className="th font-semibold col-span-2 pl-2">{tableName}</div>
            {
              sections.map((section) => (
                <div className="th font-semibold text-center capitalize" key={section.value}>{section.label}</div>
              ))
            }
          </div>
          {list.map((user, userIdx) => (
            <div
              key={user.id}
              className="grid [&>div]:px-2 border-t border-t-foreground-300"
              style={{ gridTemplateColumns: `repeat(${2 + sections?.length}, minmax(0, 1fr))` }}
            >
              {/* Avatar y nombre */}
              <div className="col-span-2 bg-foreground-200 pt-2">
                <User
                  avatarProps={{ showFallback: true, radius: 'full', src: user?.photo ? fromPhotos(user.photo) : null }}
                  name={user?.fullName}
                  classNames={{ description: 'text-foreground-500' }}
                />
              </div>

              {sections.map((section, sectionIdx) => {
                const isStudent = form.level === LEVEL.STUDENT;
                const isSelected = isStudent
                  ? user.assignedSection === section.value // Si es estudiante, compara directamente
                  : user.assignedSection.includes(section.value); // Si es profesor, busca en el array

                return (
                  <div key={`section-${sectionIdx}`} className="flex justify-center items-center">
                    <div
                      className={classNames(
                        'w-4 h-4 rounded-full border-1 cursor-pointer',
                        isSelected ? 'bg-green border-green' : 'bg-foreground-300 border-foreground-400',
                      )}
                      onClick={() => onChange(section.value, 'assignedSection', userIdx)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </main>
      </div>
    </div>
  )
}

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    school_year_id: '',
    school_grade_id: '',
  };

  // const initialPagination = {
  //   page: 1,
  //   pages: 1,
  //   total: 0,
  //   perPage: Constants.PER_PAGE,
  // };

  const initialForm = {
    school_year_id: '',
    school_grade_id: '',
    level: LEVEL.STUDENT,
    teachers: [],
    students: [],
  };
  const user = useSelector(state => state.user);
  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [schoolYears, setSchoolYears] = useState([]);
  const [grades, setGrades] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [sections, setSections] = useState([]);

  const toSelect = (items) => items.map(x => ({ ...x, value: String(x.id), label: x.name }));

  const fetchData = async () => {
    if (!canFetch || !filters.school_year_id || !filters.school_grade_id) return;
    setCanFetch(false);

    try {
      const assignaments = await SchoolService.schoolAssignments.getSectionsAssignment({ ...filters, school_id: user.id, perPage: 99 });
      const response = await SchoolService.schoolAssignments.getSections({ ...filters, school_id: user.id });
      const students = await UsersService.school.findStudents({ ...filters, school_id: user.id });
      const teachers = await UsersService.school.findTeachers({ school_id: user.id });

      const { data: studentsAssignments, professors: teachersAssignments } = assignaments;

      const _students = students.data.map(student => {
        const assignedSection = studentsAssignments.find((x) => x.student_id === student.id)?.section?.id;
        return {
          id: student.id,
          photo: student.photo,
          fullName: student.fullname,
          assignedSection: assignedSection ? String(assignedSection) : '',
        }
      })
      const _teachers = teachers.map(teacher => {
        const assignedSections = teachersAssignments
          .filter(x => x.professor_id === teacher.id)
          .map(x => String(x.section.id));

        return {
          id: teacher.id,
          photo: teacher.photo,
          fullName: teacher.fullname,
          assignedSection: assignedSections,
        }
      })
      setSections(toSelect(response));
      setForm(s => ({
        ...s,
        teachers: _teachers ?? [],
        students: _students ?? [],
      }));


      setCanFetch(true);

    } catch (error) {
      onError(String(error));
      setCanFetch(true);
    }
  }

  const isValidForm = () => {
    if (!form.school_year_id) {
      onError('Debe seleccionar el año escolar');
      return false;
    }

    if (!form.school_grade_id) {
      onError('Debe seleccionar el grado');
      return false;
    }

    if (!form.teachers.length && !form.students.length) {
      onError('Debe seleccionar al menos un estudiante o profesor');
      return false;
    }
    return true;
  }

  const fetchSelects = async () => {
    try {
      const gradesData = await SchoolService.schoolAdmin.getGrades({ school_id: user.id });
      const schoolYearsRes = await SchoolService.schoolAdmin.getSchoolarsYears();
      setSchoolYears(toSelect(schoolYearsRes));
      setGrades(toSelect(gradesData));
      setCanFetch(true)

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }

  const onChange = (value, target, index) => {
    if (target === 'assignedSection') {
      const _form = copy(form);
      const collection = form.level === LEVEL.STUDENT ? 'students' : 'teachers';

      if (form.level === LEVEL.STUDENT) {
        // Para estudiantes, solo se permite una sección a la vez
        _form[collection][index][target] = _form[collection][index][target] === value ? null : value;
      } else {
        // Para profesores, manejamos múltiples secciones
        const assignedSections = _form[collection][index][target] || [];
        const isAssigned = assignedSections.includes(value);

        _form[collection][index][target] = isAssigned
          ? assignedSections.filter(section => section !== value) // Si ya está, la quitamos
          : [...assignedSections, value]; // Si no está, la agregamos
      }

      return setForm(_form);
    }

    setForm(s => ({ ...s, [target]: value }));
  };

  const onError = (msg) => toast.error(msg);

  const filterBy = (value, target) => {
    setForm(s => ({ ...s, [target]: value, teachers: [], students: [] }));
    if (target === 'school_year_id') {
      setFilters({ ...filters, page: 1, [target]: value, school_grade_id: '' })
      return
    }
    setFilters({ ...filters, page: 1, [target]: value })
    setSections([]);
  }


  const onSubmit = async () => {
    if (!isValidForm()) return;
    try {
      const _students = form.students
        .filter(student => student.assignedSection)
        .map(student => ({ student_id: student.id, school_section_id: +student.assignedSection }));

      const _teachers = form.teachers
        .filter(teacher => teacher.assignedSection && teacher.assignedSection.length > 0)
        .flatMap(teacher =>
          teacher.assignedSection.map(sectionId => ({
            professor_id: teacher.id,
            school_section_id: +sectionId,
          }))
        );

      const data = {
        school_id: user.id,
        assignments: [..._students, ..._teachers],
      }

      await SchoolService.schoolAssignments.assignSections(data);

      toast.success('Asignación creada correctamente correctamente');
      //fetchData();
    } catch (error) {
      onError(String(error));
    }
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    fetchSelects();
  }, [])

  return {
    data,
    filterBy,
    filters,
    form,
    onChange,
    grades: grades.filter(x => x.school_year_id === +filters.school_year_id),
    isLoading: !canFetch,
    schoolYears,
    sections,
    onSubmit,
  }
}

export default AssignmentsCreate;